import { Skeleton } from "./ui/skeleton";
import { Logo } from "./Logo";
import { Menu } from "lucide-react";

const bar = "bg-neutral-200";

export function OverviewSkeleton() {
  return (
    <div className="space-y-8" data-testid="overview-skeleton">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-lg border border-neutral-200 bg-white p-6">
            <Skeleton className={`w-4 h-4 ${bar}`} />
            <Skeleton className={`h-8 w-16 mt-4 ${bar}`} />
            <Skeleton className={`h-3 w-24 mt-3 ${bar}`} />
            <Skeleton className={`h-3 w-32 mt-2 ${bar}`} />
          </div>
        ))}
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg">
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-neutral-200">
          <Skeleton className={`h-4 w-28 ${bar}`} />
          <Skeleton className={`h-7 w-44 rounded-full ${bar}`} />
        </div>
        <div className="divide-y divide-neutral-100">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="px-6 py-3 flex items-center justify-between gap-3">
              <Skeleton className={`h-4 w-44 ${bar}`} />
              <Skeleton className={`h-4 w-14 ${bar}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-neutral-200 rounded-lg p-6">
          <Skeleton className={`h-4 w-40 ${bar}`} />
          <Skeleton className={`h-64 w-full mt-4 rounded-lg ${bar}`} />
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <Skeleton className={`h-4 w-28 ${bar}`} />
          <Skeleton className={`h-64 w-full mt-4 rounded-lg ${bar}`} />
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg">
        <div className="px-6 py-4 border-b border-neutral-200">
          <Skeleton className={`h-4 w-40 ${bar}`} />
        </div>
        <div className="divide-y divide-neutral-100">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="px-6 py-3.5 flex items-start gap-3">
              <Skeleton className="mt-1.5 w-1.5 h-1.5 rounded-full bg-neutral-300 shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className={`h-3.5 w-3/4 ${bar}`} />
                <Skeleton className={`h-3 w-28 ${bar}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function EmployeeRowsSkeleton({ rows = 6 }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i}>
          <td className="px-6 py-4">
            <div className="flex items-center gap-3">
              <Skeleton className={`w-8 h-8 rounded-full shrink-0 ${bar}`} />
              <div className="space-y-1.5">
                <Skeleton className={`h-3.5 w-28 ${bar}`} />
                <Skeleton className={`h-3 w-20 ${bar}`} />
              </div>
            </div>
          </td>
          <td className="px-4 py-4"><Skeleton className={`h-3.5 w-20 ${bar}`} /></td>
          <td className="px-4 py-4"><Skeleton className={`h-6 w-24 rounded-full ${bar}`} /></td>
          <td className="px-4 py-4"><Skeleton className={`h-5 w-10 ${bar}`} /></td>
          <td className="px-4 py-4"><Skeleton className={`h-3.5 w-6 ${bar}`} /></td>
          <td className="px-4 py-4">
            <div className="flex items-center justify-end gap-1">
              <Skeleton className={`w-8 h-8 rounded-md ${bar}`} />
              <Skeleton className={`w-8 h-8 rounded-md ${bar}`} />
            </div>
          </td>
        </tr>
      ))}
    </>
  );
}

export function DeviceRowsSkeleton({ rows = 5 }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i}>
          <td className="px-6 py-4"><Skeleton className={`h-3.5 w-24 ${bar}`} /></td>
          <td className="px-4 py-4"><Skeleton className={`h-3.5 w-20 ${bar}`} /></td>
          <td className="px-4 py-4"><Skeleton className={`h-3.5 w-28 ${bar}`} /></td>
          <td className="px-4 py-4"><Skeleton className={`h-3.5 w-20 ${bar}`} /></td>
          <td className="px-4 py-4"><Skeleton className={`h-3.5 w-14 ${bar}`} /></td>
          <td className="px-4 py-4"><Skeleton className={`h-3.5 w-16 ${bar}`} /></td>
          <td className="px-4 py-4"><Skeleton className={`h-6 w-16 rounded-full ${bar}`} /></td>
        </tr>
      ))}
    </>
  );
}

export function SopsSkeleton({ count = 4 }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="sops-skeleton">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white border border-neutral-200 rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <Skeleton className={`w-9 h-9 rounded-md ${bar}`} />
              <div className="space-y-1.5">
                <Skeleton className={`h-3.5 w-36 ${bar}`} />
                <Skeleton className={`h-3 w-24 ${bar}`} />
              </div>
            </div>
            <Skeleton className={`h-6 w-20 rounded-full ${bar}`} />
          </div>
          <Skeleton className={`h-3 w-full mt-4 ${bar}`} />
          <Skeleton className={`h-3 w-2/3 mt-2 ${bar}`} />
          <div className="flex items-center justify-between mt-5 pt-4 border-t border-neutral-100">
            <Skeleton className={`h-3 w-28 ${bar}`} />
            <div className="flex items-center gap-1">
              <Skeleton className={`w-8 h-8 rounded-md ${bar}`} />
              <Skeleton className={`w-8 h-8 rounded-md ${bar}`} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function DepartmentRowsSkeleton({ rows = 4 }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Skeleton className={`w-4 h-4 ${bar}`} />
            <Skeleton className={`h-3.5 w-32 ${bar}`} />
          </div>
          <Skeleton className={`w-8 h-8 rounded-md ${bar}`} />
        </div>
      ))}
    </>
  );
}

export function EmployeeProgressSkeleton() {
  return (
    <div className="space-y-8" data-testid="progress-skeleton">
      <div>
        <Skeleton className={`h-4 w-24 ${bar}`} />
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mt-4">
          <div className="flex items-center gap-4 min-w-0">
            <Skeleton className={`w-12 h-12 rounded-full shrink-0 ${bar}`} />
            <div className="space-y-2">
              <Skeleton className={`h-7 w-40 ${bar}`} />
              <Skeleton className={`h-3.5 w-52 ${bar}`} />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Skeleton className={`h-8 w-32 rounded-full ${bar}`} />
            <Skeleton className={`h-8 w-28 rounded-full ${bar}`} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-lg border border-neutral-200 bg-white p-5">
            <Skeleton className={`h-7 w-16 ${bar}`} />
            <Skeleton className={`h-3 w-20 mt-3 ${bar}`} />
            <Skeleton className={`h-3 w-24 mt-1.5 ${bar}`} />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-neutral-200 rounded-lg p-6">
          <Skeleton className={`h-4 w-32 ${bar}`} />
          <Skeleton className={`h-64 w-full mt-4 rounded-lg ${bar}`} />
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <Skeleton className={`h-4 w-44 ${bar}`} />
          <Skeleton className={`h-64 w-full mt-4 rounded-lg ${bar}`} />
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg p-6">
        <Skeleton className={`h-4 w-32 ${bar}`} />
        <div className="space-y-3 mt-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className={`h-8 w-full ${bar}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

const shellNavRows = 5;

export function ConsoleShellSkeleton() {
  return (
    <div className="min-h-screen bg-[#f9f9f8]" data-testid="console-shell-skeleton">
      <header className="lg:hidden fixed top-0 inset-x-0 z-50 h-14 bg-white border-b border-neutral-200 flex items-center gap-3 px-4">
        <Menu className="w-5 h-5 text-neutral-300" />
        <Logo suffix="Console" />
      </header>

      <aside className="hidden lg:flex w-60 shrink-0 border-r border-neutral-200 bg-white flex-col fixed inset-y-0 left-0 z-40">
        <div className="h-16 flex items-center px-5 border-b border-neutral-200">
          <Logo suffix="Console" />
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1">
          {Array.from({ length: shellNavRows }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2.5">
              <Skeleton className={`w-4 h-4 ${bar}`} />
              <Skeleton className={`h-3.5 w-24 ${bar}`} />
            </div>
          ))}
        </nav>
        <div className="px-5 py-4 border-t border-neutral-200 space-y-2">
          <Skeleton className={`h-3 w-32 ${bar}`} />
          <Skeleton className={`h-3 w-24 ${bar}`} />
        </div>
        <div className="px-5 py-4 border-t border-neutral-200 flex items-center justify-between">
          <div className="space-y-1.5">
            <Skeleton className={`h-3.5 w-20 ${bar}`} />
            <Skeleton className={`h-3 w-16 ${bar}`} />
          </div>
          <Skeleton className={`w-8 h-8 rounded-md ${bar}`} />
        </div>
      </aside>

      <main className="lg:ml-60 min-w-0 pt-14 lg:pt-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 lg:py-10 space-y-8">
          <div className="space-y-2">
            <Skeleton className={`h-8 w-40 ${bar}`} />
            <Skeleton className={`h-3.5 w-64 ${bar}`} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-lg border border-neutral-200 bg-white p-6">
                <Skeleton className={`w-4 h-4 ${bar}`} />
                <Skeleton className={`h-8 w-16 mt-4 ${bar}`} />
                <Skeleton className={`h-3 w-24 mt-3 ${bar}`} />
              </div>
            ))}
          </div>
          <Skeleton className={`h-64 w-full rounded-lg ${bar}`} />
        </div>
      </main>
    </div>
  );
}
